import React, { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import {
  Grid,
  Stack,
  styled,
  Theme,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { SidebarContent } from "../SidebarContent";
import { IProps } from "./IProps";
import Drawer from "@mui/material/Drawer";
import { CSSObject } from "@emotion/react";
import InboxIcon from "@mui/icons-material/Inbox"; // Icon for the empty state

const drawerWidth = 270;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#FFFFFF",
  padding: theme.spacing(1.25, 3.75, 1.25, 1.25),
  width: 270,
  minHeight: 70,
}));

const openMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)}) + 1px`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)}) + 1px`,
  },
});

const OpenClosedDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "openPersistentSidebar",
})(({ theme, open }) => ({
  width: 270,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openMixin(theme),
    "& .MuiDrawer-paper": openMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export const SideBar: FunctionComponent<IProps> = ({
  onToggleSidebar,
  openSidebar,
  openPersistentSidebar,
  ...props
}): JSX.Element => {
  const { window } = props;
  const container =
    window !== undefined ? () => window().document.body : undefined;

  const DrawerContent = () => {
    if (props.isLoading) {
      return (
        <Grid
          item
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          position="sticky"
        >
          <CircularProgress />
        </Grid>
      );
    }

    if (!props.content || props.content.length === 0) {
      return (
        <Grid
          item
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
            textAlign: "center",
            px: 2,
            py: 4,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.08)",
              borderRadius: 2,
              padding: 3,
              boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
              width: "fit-content",
              maxWidth: 320, // keeps long text readable
            }}
          >
            <InboxIcon sx={{ fontSize: 48, color: "#89A3C6", mb: 1 }} />

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 500,
                color: "#E0E0E0",
                whiteSpace: "normal",
              }}
            >
              Your navigation menu has no items configured. Add them in the
              ‘User Access’ page or contact your administrator.
            </Typography>
          </Box>
        </Grid>
      );
    }

    return (
      <Stack spacing={2}>
        {props.content.map(
          (con, index): JSX.Element => (
            <SidebarContent key={index} title={con.title} links={con.links} />
          )
        )}
      </Stack>
    );
  };

  return (
    <Box>
      {/* Medium and larger screen drawer */}
      <Box
        component="nav"
        sx={{ width: { md: 270 }, flexShrink: { md: 0 } }}
        aria-label="sidebar navigation"
      >
        <OpenClosedDrawer
          variant="persistent"
          sx={{
            display: { xs: "none", sm: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              borderWidth: 0,
              backgroundColor: "#12253A",
              width: 270,
            },
          }}
          open={openPersistentSidebar}
          anchor="left"
        >
          <DrawerHeader>
            <Link to="/dealer-home">
              <Box display={{ xs: "none", sm: "none", md: "flex", lg: "flex" }}>
                <img
                  src={process.env.PUBLIC_URL + "/lp_central_cyangray.svg"}
                  alt="Logo"
                />
              </Box>
            </Link>
          </DrawerHeader>
          <DrawerContent />
        </OpenClosedDrawer>
      </Box>

      {/* Mobile Drawer */}
      <Box>
        <Drawer
          container={container}
          variant="temporary"
          open={openSidebar}
          onClose={onToggleSidebar}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "block", md: "none", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#12253A",
            },
          }}
        >
          <DrawerHeader>
            <Link to="/dealer-home">
              <Box display={{ xs: "flex", sm: "flex", md: "none", lg: "none" }}>
                <img src={process.env.PUBLIC_URL + "/lp_lite.svg"} alt="Logo" />
              </Box>
            </Link>
          </DrawerHeader>
          <DrawerContent />
        </Drawer>
      </Box>
    </Box>
  );
};
